/**
 * <s-mail-signup> — Mailing list form that lets visitors choose email, post, or both.
 *
 * Required child markup:
 *   form[data-signup-form]   — Shopify customer form (email marketing). Submits natively for email-only.
 *   form[data-postal-form]   — Hidden Shopify contact form; receives the postal details.
 *   [data-channel="email"|"post"] — Channel checkboxes.
 *   [data-post-option]       — Wrapper for the post checkbox; hidden until this element upgrades.
 *   fieldset[data-postal-fields] — Address inputs, enabled only while post is ticked.
 *   [data-postal-required]   — Address inputs that become required while post is ticked.
 *   [data-tags]              — Hidden customer tags input.
 *   [data-status]            — Live status message region.
 *
 * Strings come from data-* attributes rendered with Liquid translations.
 */
class MailSignup extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    this.form = this.querySelector('form[data-signup-form]');
    this.postalForm = this.querySelector('form[data-postal-form]');
    this.emailToggle = this.querySelector('[data-channel="email"]');
    this.postToggle = this.querySelector('[data-channel="post"]');
    this.postalFields = this.querySelector('[data-postal-fields]');
    this.status = this.querySelector('[data-status]');
    this.tags = this.querySelector('[data-tags]');
    if (!this.form || !this.postalForm || !this.postToggle) return;

    this.querySelector('[data-post-option]')?.removeAttribute('hidden');

    const { signal } = this.#controller;
    this.form.addEventListener('change', () => this.#sync(), { signal });
    this.form.addEventListener('submit', (event) => this.#onSubmit(event), { signal });
    this.#sync();

    if (this.postalForm.querySelector('[data-postal-posted]')) {
      this.#showSuccess();
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #sync() {
    const post = this.postToggle.checked;
    this.postalFields.hidden = !post;
    this.postalFields.disabled = !post;
    for (const input of this.postalFields.querySelectorAll('[data-postal-required]')) {
      input.required = post;
      input.setAttribute('aria-required', String(post));
    }
    this.tags.value = post ? 'newsletter,postal-mail' : 'newsletter';
  }

  #message(text, isError = false) {
    this.status.replaceChildren();
    const span = document.createElement('span');
    span.textContent = text;
    if (isError) span.className = 'text-critical';
    this.status.append(span);
    this.status.focus();
  }

  #showSuccess() {
    for (const child of this.form.children) {
      if (child !== this.status) child.hidden = true;
    }
    this.#message(this.dataset.success);
  }

  #labelFor(input) {
    return this.querySelector(`label[for="${input.id}"]`)?.textContent.replace('*', '').trim() ?? '';
  }

  #fillPostalForm() {
    for (const el of this.postalForm.querySelectorAll('[data-generated]')) el.remove();

    const value = (name) => this.form.querySelector(`[name="${name}"]`)?.value.trim() ?? '';
    const name = [value('contact[first_name]'), value('contact[last_name]')].filter(Boolean).join(' ');
    const channels = [this.emailToggle, this.postToggle]
      .filter((toggle) => toggle.checked)
      .map((toggle) => toggle.closest('label').textContent.trim())
      .join(', ');

    const lines = [this.dataset.bodyHeading];
    lines.push(`${this.form.querySelector('fieldset legend').textContent.trim()}: ${channels}`);
    for (const input of this.postalFields.querySelectorAll('input')) {
      if (input.value.trim()) lines.push(`${this.#labelFor(input)}: ${input.value.trim()}`);
    }

    const fields = {
      'contact[name]': name,
      'contact[email]': value('contact[email]'),
      'contact[body]': lines.join('\n'),
    };
    for (const [key, val] of Object.entries(fields)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = val;
      input.dataset.generated = '';
      this.postalForm.append(input);
    }
  }

  async #send(form, successParam) {
    const response = await fetch(form.action, {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'text/html' },
    });
    if (response.ok && response.url.includes(successParam)) return true;
    return false;
  }

  async #onSubmit(event) {
    const email = this.emailToggle.checked;
    const post = this.postToggle.checked;

    if (!email && !post) {
      event.preventDefault();
      this.#message(this.dataset.errorNone, true);
      return;
    }

    // Email only: let Shopify's customer form submit natively.
    if (!post) return;

    event.preventDefault();
    const button = this.form.querySelector('button[type="submit"]');
    button.setAttribute('aria-busy', 'true');
    this.#fillPostalForm();

    try {
      const postalSent = await this.#send(this.postalForm, 'contact_posted=true');
      if (!postalSent) {
        // Spam challenge or validation: hand over to the native contact form.
        this.postalForm.submit();
        return;
      }
      if (email) {
        const emailSent = await this.#send(this.form, 'customer_posted=true');
        if (!emailSent) {
          HTMLFormElement.prototype.submit.call(this.form);
          return;
        }
      }
      this.#showSuccess();
    } catch {
      this.#message(this.dataset.errorGeneric, true);
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('s-mail-signup')) {
  customElements.define('s-mail-signup', MailSignup);
}
